import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { useDriverId } from '../../../hooks/useDriverId';
import { useBack } from '../../../hooks/useBack';
import { formatCalendarDate } from '../../../lib/calendar-date';
import { loadStatusSkin } from '../../../lib/load-status';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import FieldPageChrome from '../../../components/field/FieldPageChrome';
import {
  FileText,
  ChevronRight,
  Loader2,
} from 'lucide-react';

export default function DriverBOLsListScreen() {
  const navigate = useNavigate();
  // A BOL's created_at is an instant, shown as the TENANT's day.

  const { driverId, isLoading: driverLoading } = useDriverId();
  const goBack = useBack('/my-work');

  const bolsQuery = useQuery({
    queryKey: ['driver-bols-list', driverId],
    queryFn: () =>
      apiClient
        .get('/trucking/loads/bols', { params: { driver_id: driverId, limit: 20 } })
        .then((r) => r.data?.data ?? r.data ?? []),
    // Without a driver id this would fetch the WHOLE company's BOLs, so the
    // query stays off rather than falling back to an unfiltered list.
    enabled: !!driverId,
    retry: false,
  });
  const bolsData = bolsQuery.data;
  const failed = queryLoadFailure(bolsQuery) != null;
  const isLoading = driverLoading || (!!driverId && bolsQuery.isLoading && !failed);
  const bols: any[] = Array.isArray(bolsData) ? bolsData : [];
  /*
   * THE STATUS IS THE SHARED load_status PALETTE (fix/driver-app-trucking).
   * This screen kept its own map: `delivered` read "Signed" in green (a
   * delivered load is still owed the receiver's signature, so that told the
   * driver it was done), and any status it did not list, `dispatched` and
   * `booked` included, fell back to "Pending Signature". Unknown values get
   * the palette's slate dash, never a real status.
   */

  // The casing field header and back arrow (fe#815): this is the BOLs tile.
  return (
    <FieldPageChrome title="BOLs" onBack={goBack}>
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-fieldiq-muted animate-spin" />
          </div>
        ) : failed ? (
          // A refused or unreachable read is not "No BOLs yet".
          <LoadFailureNotice error={queryFailureError(bolsQuery)} subject="BOLs" />
        ) : bols.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-fieldiq-muted">
            <FileText className="w-10 h-10 mb-3 opacity-40" />
            <p className="text-sm">No BOLs yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {bols.map((bol: any) => {
              const s = loadStatusSkin(bol.status);
              // The load's day, not when the row was typed in: BOL 2026-0001 was
              // scheduled Oct 12 and read "Oct 15" off created_at.
              const dateStr = formatCalendarDate(bol.scheduled_date, { month: 'short', day: 'numeric' }) ?? '—';
              return (
                <button
                  key={bol.id}
                  onClick={() => navigate(`/driver/bols/${bol.id}`)}
                  className="w-full bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 text-left flex items-center justify-between"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-fieldiq-accent">
                        BOL #{bol.bol_number || '—'}
                      </span>
                      <span
                        data-testid="driver-bol-status"
                        className={`rounded-full border border-black/20 px-2 py-0.5 text-[10px] font-black uppercase text-fi-signal-ink ${s.fill}`}
                      >
                        {s.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-fieldiq-muted">
                      <span>{bol.customer_name || bol.customer || '—'}</span>
                      {dateStr && (
                        <>
                          <span className="text-fieldiq-border">&middot;</span>
                          <span>{dateStr}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-fieldiq-muted flex-shrink-0 ml-2" />
                </button>
              );
            })}
          </div>
        )}
    </FieldPageChrome>
  );
}
